import { UserAvatar } from "@promptlayer/ui";
import { ChevronDown, ChevronRight, Edit2, Loader2, Reply, Trash2 } from "lucide-react";
import moment from "moment";
import React, { useState } from "react";

import CommentInput from "./CommentInput";
import { CommentWithReplies } from "./types";

import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfirm } from "@/context/confirm-context";
import { useUser } from "@/context/user-context";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { cn } from "@/lib/utils";
import { useGetCommentReplies } from "@/queries/changelog";

interface CommentProps {
  comment: CommentWithReplies;
  depth?: number;
  onReply?: (parentId: number, text: string) => void;
  onDelete?: (commentId: number) => void;
  onUpdate?: (commentId: number, text: string) => void;
  isPending?: boolean;
  isOptimistic?: boolean;
  isCommentPending?: (commentId: number) => boolean;
  isCommentOptimistic?: (commentId: number) => boolean;
}

const formatDate = (dateString: string) => moment(dateString).fromNow();

const Comment = ({
  comment,
  depth = 0,
  onReply,
  onDelete,
  onUpdate,
  isPending = false,
  isOptimistic = false,
  isCommentPending,
  isCommentOptimistic,
}: CommentProps) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [shouldFetchReplies, setShouldFetchReplies] = useState(false);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const user = useUser();
  const activeWorkspace = useActiveWorkspace();
  const { confirm } = useConfirm();

  // Lazy-load replies when user requests them
  const {
    data: repliesData,
    isLoading: isLoadingReplies,
    refetch: fetchReplies,
  } = useGetCommentReplies({
    workspaceId: activeWorkspace?.id ?? 0,
    commentId: comment.id,
    enabled: true,
  });

  // Auto-show replies once they're loaded
  React.useEffect(() => {
    if (shouldFetchReplies && repliesData && !isLoadingReplies) {
      setShowReplies(true);
    }
  }, [shouldFetchReplies, repliesData, isLoadingReplies]);

  const handleShowReplies = () => {
    if (showReplies) {
      // Just hide replies
      setShowReplies(false);
    } else if (!repliesData) {
      // First time showing replies - start fetching
      setShouldFetchReplies(true);
    } else {
      // Data already loaded, just show it
      setShowReplies(true);
    }
  };

  const handleReply = async (text: string) => {
    if (onReply) {
      setIsSubmittingReply(true);
      try {
        await onReply(comment.id, text);
        setShowReplyInput(false);
        // After creating a reply, expand and refresh the replies list
        setShouldFetchReplies(true);
        setShowReplies(true);
        fetchReplies();
      } finally {
        setIsSubmittingReply(false);
      }
    }
  };

  const handleCancelReply = () => {
    setShowReplyInput(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditText(comment.text);
  };

  const handleSaveEdit = async (text: string) => {
    if (onUpdate && text.trim()) {
      setIsSubmittingEdit(true);
      try {
        await onUpdate(comment.id, text);
        setIsEditing(false);
      } finally {
        setIsSubmittingEdit(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText(comment.text);
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Delete Comment",
      message: "Are you sure you want to delete this comment? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      headerVariant: ConfirmationModalVariant.destructive,
      saveButtonVariant: "destructive",
      exitButtonVariant: "outline",
    });

    if (confirmed && onDelete) {
      onDelete(comment.id);
    }
  };

  const hasReplies = comment.replies > 0;
  const replyCount = comment.replies;
  const isOwnComment = user.user?.id === comment.user_id;
  const commentIsPending = isPending || isCommentPending?.(comment.id);
  const commentIsOptimistic = isOptimistic || isCommentOptimistic?.(comment.id) || comment.id < 0;

  const replies = repliesData?.items ?? [];

  return (
    <div className={`${depth > 0 ? "ml-8 mt-2" : "mt-2"}`}>
      <div
        className={cn(
          "group rounded-lg p-2 transition-colors hover:bg-gray-50",
          commentIsPending && "opacity-60",
          commentIsOptimistic && "animate-pulse bg-blue-50/50",
        )}
      >
        <div className="flex gap-3">
          <UserAvatar email={comment.user?.email} className="h-7 w-7 flex-shrink-0" />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="text-sm font-medium text-foreground">
                {comment.user?.name || comment.user?.email?.split("@")[0].replace(/[._]/g, " ")}
              </span>
              <span className="text-xs text-muted-foreground">
                {commentIsOptimistic ? "Just now" : formatDate(comment.created_at)}
              </span>
              {comment.created_at !== comment.updated_at && !commentIsOptimistic && (
                <span className="text-xs text-muted-foreground">(edited)</span>
              )}
              {commentIsOptimistic && (
                <span className="flex items-center gap-1 text-xs text-blue-600">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Posting...
                </span>
              )}
              {commentIsPending && !commentIsOptimistic && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving...
                </span>
              )}
            </div>

            <div className="mt-2">
              <CommentInput
                initialValue={editText}
                onSubmit={handleSaveEdit}
                readOnly={!isEditing}
                onCancel={handleCancelEdit}
                placeholder="Edit your comment..."
                submitButtonText="Save"
                autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                showActionsOnMount
                isSubmitting={isSubmittingEdit}
              />
            </div>

            {/* Actions */}
            {!isEditing && !commentIsOptimistic && (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:bg-gray-100 hover:text-foreground"
                  onClick={() => setShowReplyInput(!showReplyInput)}
                  disabled={commentIsPending}
                >
                  <Reply className="h-3.5 w-3.5" />
                  Reply
                </Button>

                {isOwnComment && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:bg-gray-100 hover:text-foreground"
                      onClick={handleEdit}
                      disabled={commentIsPending}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                      Edit
                    </Button>

                    {onDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:bg-gray-100 hover:text-destructive"
                        onClick={handleDelete}
                        disabled={commentIsPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </Button>
                    )}
                  </>
                )}

                {hasReplies && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:bg-gray-100 hover:text-foreground"
                    onClick={handleShowReplies}
                    disabled={isLoadingReplies || commentIsPending}
                  >
                    {showReplies ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    {isLoadingReplies ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      `${replyCount} ${replyCount === 1 ? "reply" : "replies"}`
                    )}
                  </Button>
                )}
              </div>
            )}

            {/* Reply Input */}
            {showReplyInput && !isEditing && (
              <div className="mt-3">
                <CommentInput
                  onSubmit={handleReply}
                  onCancel={handleCancelReply}
                  placeholder="Write a reply..."
                  submitButtonText="Reply"
                  autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                  showActionsOnMount
                  isSubmitting={isSubmittingReply}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading skeleton for replies */}
      {isLoadingReplies && shouldFetchReplies && (
        <div className="ml-8 mt-2 space-y-2">
          <div className="flex gap-3 rounded-lg p-2">
            <Skeleton className="h-7 w-7 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </div>
      )}

      {/* Nested Replies */}
      {showReplies && replies.length > 0 && (
        <div className="duration-200 animate-in fade-in slide-in-from-top-2">
          {replies.map(reply => (
            <Comment
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              onReply={onReply}
              onDelete={onDelete}
              onUpdate={onUpdate}
              isCommentPending={isCommentPending}
              isCommentOptimistic={isCommentOptimistic}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Comment;
export type { CommentWithReplies };
